import produce from 'immer';
import {Spec} from 'immutability-helper';
import React from 'react';

import {assertAndContinue} from 'Asserts';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import DataTableConfigEditable from 'notebook/data-tables/DataTableConfigEditable';
import {DataTableConfig} from 'notebook/data-tables/DataTableConstants';
import * as DataTableUtil from 'notebook/data-tables/DataTableUtil';
import MediocrePlugins from 'notebook/mediocre/base-plugin/plugins';
import {StoreDependencyMap} from 'notebook/mediocre/EditorTypes';
import {TableItemProps} from 'notebook/mediocre/MediocreConstants';
import {MediocreSearchResultProps} from 'notebook/mediocre/search/SearchResultProps';
import {MediocreSearchResult, TextMatcher} from 'notebook/mediocre/search/SearchResults';
import {
  buildTableSearchResult,
  doesTableCellMatch,
  getSearchResultsForColumnNames,
  getSearchResultsForTableName,
} from 'notebook/mediocre/search/TableSearchUtil';
import {NoteEditor, NoteEditorPlugin, NoteEditorProps} from 'notebook/notes/NoteEditorTypes';
import {RegistrationTableConfig} from 'notebook/registration-tables/RegistrationTableConstants';
import * as RegistrationTableUtil from 'notebook/registration-tables/RegistrationTableUtil';
import {CollapsedTableState} from 'notebook/tables/collapse/reduceCollapsedTablesUI';
import {OutputTableMap} from 'notebook/tables/excess/Evaluator';
import {Table, TableData} from 'notebook/tables/TableConstants';
import {NotePart} from 'notebook/util/NoteUtilConstants';
import notifications from 'notifications';
import {MODEL_NAMES} from 'results/ResultsNamingConstants';
import APIUtil from 'util/APIUtil';

type DataTableConfigItemProps = TableItemProps & {registrationTableConfigs: Array<RegistrationTableConfig>};

const DATA_TABLE_CONFIG_PLUGIN: NoteEditorPlugin = {
  type: ModelTypeConstants.DATA_TABLE_CONFIG,
  typeClass: 'is-table',
  title: `Structured ${MODEL_NAMES.Result_Table}`,
  icon: 'note-table',
  component: {
    selectionInit: MediocrePlugins.table.selectionInit,
    selectionAtStart: MediocrePlugins.table.selectionAtStart,
    selectionAtEnd: MediocrePlugins.table.selectionAtEnd,
    required: [],
    // Plugin method are always called using Function.prototype.call,
    // so `this` refers to the editor component.
    getItemProps(
      this: NoteEditor,
      item: NotePart & {item: DataTableConfig},
      index: number
    ): DataTableConfigItemProps {
      return {
        ...MediocrePlugins.getTableItemProps.call(this, item, index),
        registrationTableConfigs: RegistrationTableUtil.getRegistrationTableConfigsInNote(this.props.note),
      };
    },

    getIsCollapsed(
      item: NotePart & {item: DataTableConfig},
      editorProps: NoteEditorProps,
      collapsedStateById: CollapsedTableState
    ) {
      // Component doesnt enable collapsing.
      return false;
    },

    // Plugin method are always called using Function.prototype.call,
    // so `this` refers to the editor component.
    make(
      this: NoteEditor,
      item: NotePart & {item: DataTableConfig},
      index: number,
      editorProps: NoteEditorProps,
      itemProps: DataTableConfigItemProps,
      searchResultProps: MediocreSearchResultProps
    ): JSX.Element {
      const cid = editorProps.getCid(item);
      const dataTableConfig = item.item;
      return (
        <DataTableConfigEditable
          {...itemProps}
          dataTableConfig={dataTableConfig}
          updateHandler={this.tableUpdateHandler}
          cid={cid}
          editor={this}
          editorProps={editorProps}
          indentation={typeof this.getIndentation === 'function' ? this.getIndentation(item) : undefined}
          index={index}
          // Methods proxied through to TableEditable component
          mouseDownHandler={this.itemMouseDownHandler}
          mouseOverHandler={this.itemMouseOverHandler}
          {...searchResultProps}
        />
      );
    },
  },
  itemToHTML($el: JQuery, item: NotePart & {item: DataTableConfig}): JQuery {
    // TODO(saif)
    return $el;
  },
  // Plugin method are always called using Function.prototype.call,
  // so `this` refers to the editor component.
  itemFromHTML(this: NoteEditor, $el: JQuery, item: NotePart & {item: DataTableConfig}): NotePart | null {
    const fileType = APIUtil.apiIdToType(this.props.fileId);
    if (
      [ModelTypeConstants.STAGE_ENTRY, ModelTypeConstants.ENTRY, ModelTypeConstants.SUBTEMPLATE].includes(
        fileType
      )
    ) {
      notifications.warn(`Cannot paste ${MODEL_NAMES.result_tables} from template to entry/sub-template.`);
      return null;
    } else if (fileType === ModelTypeConstants.TEMPLATE) {
      return produce(item, (draftItem) => {
        draftItem.item = DataTableUtil.convertDataTableConfigToStandalone(draftItem.item as DataTableConfig);
      });
    } else {
      return item;
    }
  },
  // This must stay in sync with table.name returned from getContent
  getSpreadsheetTableName({item}: NotePart & {item: DataTableConfig}): string {
    return DataTableUtil.getDataTableConfigName(item);
  },
  getContent({item}: NotePart & {item: DataTableConfig}): {table: Table} {
    return {
      table: DataTableUtil.convertDataTableConfigToTable(item, true /* indicateCustomColumns */),
    };
  },
  setContent({item, table}: {item?: DataTableConfig; table?: TableData}): Spec<NotePart> {
    if (item) {
      return {$merge: {item}};
    } else if (table) {
      return {
        item: {
          $apply(dataTableConfig: DataTableConfig): DataTableConfig {
            return DataTableUtil.mergeTableUpdateIntoDataTableConfig(dataTableConfig, table);
          },
        },
      };
    } else {
      assertAndContinue(false, 'setContent invoked without content to set.');
      return {};
    }
  },
  getStoreDependenciesForSpreadsheetEval(item: NotePart & {item: DataTableConfig}): StoreDependencyMap {
    return {};
  },
  *getSearchResults(
    item: NotePart & {item: DataTableConfig},
    matcher: TextMatcher,
    searchOverFormulas: boolean,
    computedTables: OutputTableMap | null
  ): Generator<MediocreSearchResult> {
    // Search over the table name
    yield* getSearchResultsForTableName(matcher, item.item.name, item.cid);

    // Search over the column names
    const columnNames = item.item.columns.map((column) => column.name ?? '');
    yield* getSearchResultsForColumnNames(matcher, columnNames, item.cid);

    const computedTable = computedTables?.[item.item.name];
    const i = 0; // DataTableConfig is a single row comprised of the defaultValue on each column
    for (const [j, column] of item.item.columns.entries()) {
      if (
        doesTableCellMatch(
          matcher,
          column.defaultValue,
          computedTable ? computedTable[i][j] : null,
          searchOverFormulas
        )
      ) {
        yield buildTableSearchResult(item.cid, i, j);
      }
    }
  },
};

/*
 * This plugin is no longer supported, please do not further proliferate its use.
 */
export default DATA_TABLE_CONFIG_PLUGIN;
